import {
  type Category,
  convertColor,
  type FermentableType,
  type HopForm,
  type HopUse,
  type RecipeLine as MathLine,
  NEW_RECIPE_TANK_DAYS,
  type RecipeHopForm,
  type SpecValue,
  startingValue,
  withGrownForm,
} from "@bl/domain";
import {
  type BrewSystem,
  type Item,
  newKey,
  type RecipeDetail,
  type RecipeDraft,
  type RecipeInput,
} from "../api";
import { clearLocal, readLocal } from "../autosave";
import { pickedFrom } from "../forms/BatchFields";

/**
 * The recipe builder (PLAN.md, "Slice 2"): the recipe on the left, and on the right everything worked
 * out from it as it is typed, by the same brewing math the server uses. Writing a recipe uses nothing;
 * the inventory panel says what it would need against what is on hand.
 */

export const LITERS_PER_GALLON = 3.785411784;
export const GRAMS_PER_POUND = 453.59237;
export const GRAMS_PER_OUNCE = 28.349523125;
const toF = (c: number) => (c * 9) / 5 + 32;
const toC = (f: number) => ((f - 32) * 5) / 9;
/** A number as the API stores it: a decimal string, no float noise past six places. */
export const exact = (n: number) => String(Number(n.toFixed(6)));
export const num = (s: string) => (s.trim() === "" ? Number.NaN : Number(s));
export const shown = (n: number, places: number) => String(Number(n.toFixed(places)));
export type Kind = "fermentable" | "hop" | "yeast" | "other";
export interface DraftLine {
  key: string;
  id?: string;
  kind: Kind;
  name: string;
  /**
   * What the line was called before Replace made it one of the brewery's items (Scott's Jam,
   * 2026-10-08), kept unshown for the next recipe's suggestion.
   */
  replacedName: string | null;
  itemId: string | null;
  itemName: string | null;
  /** The linked hop's form. */
  itemHopForm: HopForm | null;
  origin: string | null;
  /** Pounds for a fermentable, ounces for a hop. */
  amount: string;
  fermentableType: FermentableType;
  yieldPct: string;
  colorLovibond: string;
  alphaPct: string;
  hopForm: RecipeHopForm;
  hopUse: HopUse;
  minutes: string;
  tempF: string;
  days: string;
  lab: string;
  productId: string;
  yeastForm: "dry" | "liquid" | "slurry" | "";
  packages: string;
  /** A yeast's expected attenuation: one figure, not a range (Scott's Jam, 2026-10-02). */
  attenuationPct: string;
  use: string;
  amountText: string;
}
export interface Draft {
  name: string;
  type: RecipeInput["type"];
  brewer: string;
  description: string;
  notes: string;
  tasteNotes: string;
  style: RecipeInput["style"];
  /** The brew system its Batch figures were filled from; the figures below are the recipe's own. */
  brewSystemId: string | null;
  batchGal: string;
  boilMinutes: string;
  efficiencyPct: string;
  boilOffGal: string;
  trubGal: string;
  coolingLossPct: string;
  altitudeFeet: string;
  mashTempF: string;
  mashMinutes: string;
  /** Steps after the first, kept as they were: step mashes come with brew systems. */
  laterSteps: RecipeInput["mashSteps"];
  /** The beer's, not the brew system's (Scott's Jam, 2026-10-02). */
  carbonationVols: string;
  /**
   * How far the brewer expects it to ferment: the attenuation or the FG, whichever was typed last, the
   * other empty and worked out from the OG. Both empty leaves it to the yeast.
   */
  expectedAttenuationPct: string;
  expectedFg: string;
  /**
   * Whole days in a fermenter and then a brite tank (Tyler, 2026-10-05, with the tank schedule), the
   * fermenter's as fermentation, dry hop and lager/crash (Scott's Jam, 2026-10-08). Every recipe has its
   * own (Scott's Jam, 2026-10-08), so none can be left empty.
   */
  fermentationDays: string;
  dryHopDays: string;
  lagerCrashDays: string;
  briteDays: string;
  lines: DraftLine[];
  /**
   * A saved recipe's changes go to its next version rather than over it. Nothing is written until Save
   * (Scott's Jam, 2026-10-02), so a version started and left unsaved leaves the recipe as it was.
   */
  newVersion?: boolean;
  /**
   * Or as a recipe of its own, at version 1, leaving this one as it was (Tyler, 2026-10-02). Written
   * only on Save too, and it wins over `newVersion`.
   */
  newRecipe?: boolean;
  /** The recipe a new one was copied from with Copy on its page, to say so and go back to. */
  copiedFrom?: { id: string; name: string };
}
/**
 * What a line is called: once linked, its item (Scott's Jam, 2026-10-08: "once an item is linked, we
 * don't need to know what it was called in its legacy program"), as the recipe's page names it.
 */
export const shownName = (l: Pick<DraftLine, "name" | "itemName" | "itemHopForm">) =>
  l.itemName ? withGrownForm(l.itemName, l.itemHopForm) : l.name;
export function blankLine(kind: Kind, name: string): DraftLine {
  return {
    key: newKey(),
    kind,
    name,
    replacedName: null,
    itemId: null,
    itemName: null,
    itemHopForm: null,
    origin: null,
    amount: "",
    fermentableType: "grain",
    yieldPct: "",
    colorLovibond: "",
    alphaPct: "",
    hopForm: "pellet",
    hopUse: "boil",
    minutes: "60",
    tempF: "170",
    days: "5",
    lab: "",
    productId: "",
    yeastForm: "",
    packages: "1",
    attenuationPct: "",
    use: "",
    amountText: "",
  };
}
const str = (v: string | null | undefined) => v ?? "";
const fig = (v: string | null | undefined, places = 3) =>
  v == null ? "" : shown(Number(v), places);
export function draftOf(r: RecipeDetail): Draft {
  const [first, ...rest] = r.mashSteps;
  return {
    name: r.name,
    type: r.type,
    brewer: str(r.brewer),
    description: str(r.description),
    notes: str(r.notes),
    tasteNotes: str(r.tasteNotes),
    style: r.style,
    brewSystemId: r.brewSystem?.id ?? null,
    batchGal: shown(Number(r.settings.batchLiters) / LITERS_PER_GALLON, 2),
    boilMinutes: fig(r.settings.boilMinutes, 0),
    efficiencyPct: fig(r.settings.efficiencyPct, 1),
    boilOffGal: shown(Number(r.settings.boilOffLiters) / LITERS_PER_GALLON, 2),
    trubGal: shown(Number(r.settings.trubLossLiters) / LITERS_PER_GALLON, 2),
    coolingLossPct: fig(r.settings.coolingLossPct, 1),
    altitudeFeet: fig(r.settings.altitudeFeet, 0),
    mashTempF: first ? shown(toF(first.tempC), 1) : "152",
    mashMinutes: first ? String(first.minutes) : "60",
    laterSteps: rest,
    carbonationVols: fig(r.carbonationVols, 2),
    expectedAttenuationPct: fig(r.expectedAttenuationPct, 1),
    expectedFg: fig(r.expectedFg, 4),
    fermentationDays: String(r.fermentationDays),
    dryHopDays: String(r.dryHopDays),
    lagerCrashDays: String(r.lagerCrashDays),
    briteDays: String(r.briteDays),
    lines: r.lines.map((l) => ({
      ...blankLine(l.kind, l.name),
      id: l.id,
      replacedName: l.replacedName,
      itemId: l.item?.id ?? null,
      itemName: l.item?.name ?? null,
      itemHopForm: l.item?.hopForm ?? null,
      origin: l.origin,
      amount:
        l.grams == null
          ? ""
          : shown(Number(l.grams) / (l.kind === "hop" ? GRAMS_PER_OUNCE : GRAMS_PER_POUND), 3),
      fermentableType: (l.fermentableType ?? "grain") as FermentableType,
      yieldPct: fig(l.yieldPct, 2),
      colorLovibond: fig(l.colorLovibond, 2),
      alphaPct: fig(l.alphaPct, 2),
      hopForm: (l.hopForm ?? "pellet") as RecipeHopForm,
      hopUse: (l.hopUse ?? "boil") as HopUse,
      minutes: fig(l.minutes, 1),
      tempF: l.tempC == null ? "170" : shown(toF(Number(l.tempC)), 1),
      days: fig(l.days, 1),
      lab: str(l.lab),
      productId: str(l.productId),
      yeastForm: (l.yeastForm ?? "") as DraftLine["yeastForm"],
      packages: fig(l.packages, 2),
      attenuationPct: fig(l.attenuationPct, 2),
      use: str(l.use),
      amountText: str(l.amountText),
    })),
  };
}
/**
 * The brew system a new recipe starts on: the newest recipe's, most likely this one's too, or the
 * brewery's only one.
 */
export function startingSystem(from: RecipeDetail | undefined, systems: BrewSystem[]) {
  const newest = systems.find((s) => s.id === from?.brewSystem?.id);
  return newest ?? (systems.length === 1 ? systems[0] : undefined);
}
/**
 * A new recipe copied from one (Tyler, 2026-10-02): the whole recipe, its links to inventory with it,
 * named as a copy so the list does not show two recipes by one name.
 */
export function copyOf(r: RecipeDetail): Draft {
  const d = draftOf(r);
  return {
    ...d,
    name: `${r.name} copy`,
    copiedFrom: { id: r.id, name: r.name },
    lines: d.lines.map(({ id: _id, ...l }) => l),
  };
}
export function blankDraft(from?: RecipeDetail, system?: BrewSystem): Draft {
  const draft = blankSettings(from);
  return system
    ? { ...draft, ...pickedFrom(system, draft), brewSystemId: system.id }
    : { ...draft, brewSystemId: null };
}
function blankSettings(from?: RecipeDetail): Omit<Draft, "brewSystemId"> {
  const s = from?.settings;
  return {
    name: "",
    type: "all_grain",
    brewer: from?.brewer ?? "",
    description: "",
    notes: "",
    tasteNotes: "",
    style: null,
    // A new recipe starts from the newest one's batch and brewhouse, which is most likely this one's.
    batchGal: s ? shown(Number(s.batchLiters) / LITERS_PER_GALLON, 2) : "217",
    boilMinutes: s ? fig(s.boilMinutes, 0) : "60",
    efficiencyPct: s ? fig(s.efficiencyPct, 1) : "75",
    boilOffGal: s ? shown(Number(s.boilOffLiters) / LITERS_PER_GALLON, 2) : "0",
    trubGal: s ? shown(Number(s.trubLossLiters) / LITERS_PER_GALLON, 2) : "0",
    coolingLossPct: s ? fig(s.coolingLossPct, 1) : "4",
    altitudeFeet: s ? fig(s.altitudeFeet, 0) : "0",
    mashTempF: "152",
    mashMinutes: "60",
    laterSteps: [],
    carbonationVols: "2.4",
    expectedAttenuationPct: "",
    expectedFg: "",
    fermentationDays: String(NEW_RECIPE_TANK_DAYS.fermentation),
    dryHopDays: String(NEW_RECIPE_TANK_DAYS.dryHop),
    lagerCrashDays: String(NEW_RECIPE_TANK_DAYS.lagerCrash),
    briteDays: String(NEW_RECIPE_TANK_DAYS.brite),
    lines: [],
  };
}
/** What a line still needs before it can be saved, by its kind. */
export function missing(l: DraftLine): string[] {
  const need: [boolean, string][] =
    l.kind === "fermentable"
      ? [
          [Number.isFinite(num(l.amount)), "amount"],
          [Number.isFinite(num(l.yieldPct)), "yield"],
          [Number.isFinite(num(l.colorLovibond)), "color"],
        ]
      : l.kind === "hop"
        ? [
            [Number.isFinite(num(l.amount)), "amount"],
            [Number.isFinite(num(l.alphaPct)), "alpha"],
          ]
        : l.kind === "yeast"
          ? [[Number.isFinite(num(l.attenuationPct)), "attenuation"]]
          : [];
  return [...new Set(need.filter(([ok]) => !ok).map(([, what]) => what))];
}
export function settingsOf(d: Draft) {
  return {
    batchLiters: num(d.batchGal) * LITERS_PER_GALLON,
    boilMinutes: num(d.boilMinutes),
    efficiencyPct: num(d.efficiencyPct),
    boilOffLiters: (num(d.boilOffGal) || 0) * LITERS_PER_GALLON,
    trubLossLiters: (num(d.trubGal) || 0) * LITERS_PER_GALLON,
    coolingLossPct: num(d.coolingLossPct) || 0,
    altitudeFeet: num(d.altitudeFeet) || 0,
    mashTempC: Number.isFinite(num(d.mashTempF)) ? toC(num(d.mashTempF)) : null,
    expectedAttenuationPct: Number.isFinite(num(d.expectedAttenuationPct))
      ? num(d.expectedAttenuationPct)
      : null,
    expectedFg: Number.isFinite(num(d.expectedFg)) ? num(d.expectedFg) : null,
  };
}
export function mathLine(l: DraftLine): MathLine {
  const or0 = (s: string) => (Number.isFinite(num(s)) ? num(s) : 0);
  if (l.kind === "fermentable") {
    return {
      kind: "fermentable",
      grams: or0(l.amount) * GRAMS_PER_POUND,
      type: l.fermentableType,
      yieldPct: or0(l.yieldPct),
      colorLovibond: or0(l.colorLovibond),
    };
  }
  if (l.kind === "hop") {
    return {
      kind: "hop",
      grams: or0(l.amount) * GRAMS_PER_OUNCE,
      alphaPct: or0(l.alphaPct),
      form: l.hopForm,
      use: l.hopUse,
      minutes: or0(l.minutes),
      tempC: l.hopUse === "whirlpool" ? toC(or0(l.tempF)) : null,
    };
  }
  if (l.kind === "yeast") {
    return { kind: "yeast", attenuationPct: or0(l.attenuationPct) };
  }
  return { kind: "other" };
}
/** Days typed, as whole days; none when empty. */
const wholeDays = (typed: string) => (typed.trim() === "" ? undefined : Math.round(num(typed)));
export function inputOf(d: Draft): RecipeInput {
  const s = settingsOf(d);
  const opt = (v: string) => (Number.isFinite(num(v)) ? exact(num(v)) : null);
  return {
    name: d.name.trim(),
    type: d.type,
    brewer: d.brewer.trim() || null,
    description: d.description.trim() || null,
    notes: d.notes.trim() || null,
    tasteNotes: d.tasteNotes.trim() || null,
    style: d.style ?? null,
    brewSystemId: d.brewSystemId ?? null,
    settings: {
      batchLiters: exact(s.batchLiters),
      boilMinutes: exact(s.boilMinutes),
      efficiencyPct: exact(s.efficiencyPct),
      boilOffLiters: exact(s.boilOffLiters),
      trubLossLiters: exact(s.trubLossLiters),
      coolingLossPct: exact(s.coolingLossPct),
      altitudeFeet: exact(s.altitudeFeet),
    },
    mashSteps: [
      {
        name: "Mash In",
        tempC: Number((s.mashTempC ?? toC(152)).toFixed(4)),
        minutes: num(d.mashMinutes) || 60,
      },
      ...d.laterSteps,
    ],
    carbonationVols: opt(d.carbonationVols),
    expectedAttenuationPct: opt(d.expectedAttenuationPct),
    expectedFg: opt(d.expectedFg),
    fermentationDays: wholeDays(d.fermentationDays),
    dryHopDays: wholeDays(d.dryHopDays),
    lagerCrashDays: wholeDays(d.lagerCrashDays),
    briteDays: wholeDays(d.briteDays),
    lines: d.lines.map((l) => {
      const base = {
        ...(l.id ? { id: l.id } : {}),
        kind: l.kind,
        name: l.name.trim(),
        replacedName: l.replacedName,
        itemId: l.itemId,
        origin: l.origin,
      };
      if (l.kind === "fermentable") {
        return {
          ...base,
          grams: exact(num(l.amount) * GRAMS_PER_POUND),
          fermentableType: l.fermentableType,
          yieldPct: opt(l.yieldPct),
          colorLovibond: opt(l.colorLovibond),
        };
      }
      if (l.kind === "hop") {
        return {
          ...base,
          grams: exact(num(l.amount) * GRAMS_PER_OUNCE),
          alphaPct: opt(l.alphaPct),
          hopForm: l.hopForm,
          hopUse: l.hopUse,
          minutes: l.hopUse === "dry_hop" ? null : opt(l.minutes),
          tempC: l.hopUse === "whirlpool" ? exact(toC(num(l.tempF))) : null,
          days: l.hopUse === "dry_hop" ? opt(l.days) : null,
        };
      }
      if (l.kind === "yeast") {
        return {
          ...base,
          lab: l.lab.trim() || null,
          productId: l.productId.trim() || null,
          yeastForm: l.yeastForm || null,
          packages: opt(l.packages),
          attenuationPct: opt(l.attenuationPct),
        };
      }
      return { ...base, use: l.use.trim() || null, amountText: l.amountText.trim() || null };
    }),
  };
}
type SpecLike = Item["specs"][number];
function typical(specs: readonly SpecLike[], property: string): number | null {
  const s = specs.find((x) => x.property === property);
  if (s?.kind !== "numeric" || !s.qualifier || !s.unit) return null;
  const value = startingValue({
    kind: "numeric",
    qualifier: s.qualifier,
    min: s.min,
    max: s.max,
    unit: s.unit,
  } as SpecValue);
  if (value === null) return null;
  if (property === "color" && (s.unit === "srm" || s.unit === "ebc")) {
    return Number(convertColor(value, s.unit, "lovibond").toFixed(2));
  }
  return Number(value.toFixed(3));
}
const FERMENTABLE_BY_CATEGORY: Record<string, FermentableType> = {
  malt: "grain",
  sugar: "sugar",
  adjunct: "adjunct",
};
/**
 * A line made with one of the brewery's items, its figures from the item's typical specs, or the bound
 * where that is all its source gives (RahrBSG's "≥ 79.5" extract).
 */
export function lineFromItem(kind: Kind, it: Item): DraftLine {
  const line: DraftLine = {
    ...blankLine(kind, it.displayName),
    itemId: it.id,
    itemName: it.displayName,
    itemHopForm: it.hopForm,
    origin: it.origin,
  };
  if (kind === "fermentable") {
    line.fermentableType = FERMENTABLE_BY_CATEGORY[it.category] ?? "grain";
    line.yieldPct = String(typical(it.specs, "extract_fine") ?? "");
    line.colorLovibond = String(typical(it.specs, "color") ?? "");
  } else if (kind === "hop") {
    line.alphaPct = String(typical(it.specs, "alpha_acid") ?? "");
    line.hopForm = it.hopForm === "whole_cone" || /whole cone/i.test(it.name) ? "leaf" : "pellet";
  } else if (kind === "yeast") {
    // One expected figure from the maker's range: its middle (Scott's Jam, 2026-10-02).
    line.attenuationPct = String(typical(it.specs, "attenuation") ?? "");
    line.yeastForm = (it.yeastForm ?? "") as DraftLine["yeastForm"];
    line.lab = it.manufacturer ?? "";
    line.productId = it.specs.find((s) => s.property === "lab_code")?.text ?? "";
  }
  return line;
}
/**
 * A line replaced with one of the brewery's items (Scott's Jam, 2026-10-08: an imported "Pale Malt (2 Row)
 * UK" is White Oak's Canada Malting 2 Row). It becomes the item, with its name and figures, and keeps
 * its amount and how it is used: when a hop goes in, how a fermentable is mashed. A figure the item
 * does not have stays as the recipe had it; what the item is (its lab, code and origin) is the item's.
 * Only the builder does this: linking on a recipe's page leaves the figures alone (Tyler, 2026-10-08).
 *
 * The name the line had is kept, unless it was already the item's, so another recipe's line of that
 * name is offered the same item.
 */
export function replacedBy(old: DraftLine, made: DraftLine): DraftLine {
  const figure = (mine: string, theirs: string) => (theirs.trim() ? theirs : mine);
  const was = old.replacedName ?? (old.name !== old.itemName ? old.name : null);
  return {
    ...old,
    name: made.name,
    replacedName: was && was !== made.name ? was : null,
    itemId: made.itemId,
    itemName: made.itemName,
    itemHopForm: made.itemHopForm,
    origin: made.origin,
    yieldPct: figure(old.yieldPct, made.yieldPct),
    colorLovibond: figure(old.colorLovibond, made.colorLovibond),
    alphaPct: figure(old.alphaPct, made.alphaPct),
    // A hop is the form it comes in, where the item says; an item that does not say leaves the line's.
    hopForm: made.itemHopForm || /whole cone/i.test(made.name) ? made.hopForm : old.hopForm,
    lab: made.lab,
    productId: made.productId,
    yeastForm: made.yeastForm || old.yeastForm,
    attenuationPct: figure(old.attenuationPct, made.attenuationPct),
  };
}
/**
 * The recipe's lines with one replaced, and every other line of the same name and kind made with the
 * same item, or with none: Bro names Columbus on four lines, and they are one hop, each keeping its own
 * amount and time.
 */
export function replaceIn(lines: DraftLine[], key: string, made: DraftLine): DraftLine[] {
  const picked = lines.find((l) => l.key === key);
  if (!picked) return lines;
  const name = picked.name.trim().toLowerCase();
  const same = (l: DraftLine) =>
    l.key === key ||
    (l.kind === picked.kind && l.itemId === picked.itemId && l.name.trim().toLowerCase() === name);
  return lines.map((l) => (same(l) ? replacedBy(l, made) : l));
}
export const KIND_CATEGORIES: Record<Kind, readonly Category[]> = {
  fermentable: ["malt", "sugar", "adjunct"],
  hop: ["hop"],
  yeast: ["yeast"],
  other: ["adjunct", "sugar", "brewing_aid", "water", "chemical", "supplies"],
};
export const KIND_LABELS: Record<Kind, string> = {
  fermentable: "Grain & sugar",
  hop: "Hops",
  yeast: "Yeast",
  other: "Other",
};
/*
 * Drafts (Tyler, 2026-10-01): the builder saves itself as it is typed into and as it is left, the way
 * brew day and the invoice review do, so a recipe left mid-word is waiting in the builder. A new recipe
 * is a draft until it is first saved; changes to a recipe are a draft until they are saved over it, or
 * as its next version, so a version and its history change only when Save is pressed.
 */

/**
 * The shape drafts are kept in. 2 since 2026-10-22, when the builder stopped bringing older drafts up
 * to date (Tyler): a draft or a device's copy marked any other way is let go, never loaded.
 */
export const DRAFT_SHAPE = 2;
/** What a draft keeps: the page as typed, marked with its shape so a later page never misreads it. */
export interface Kept {
  v: typeof DRAFT_SHAPE;
  draft: Draft;
}
/** This device's copy of what has not reached the server: the draft's id with the page, marked too. */
export interface Copy {
  v: typeof DRAFT_SHAPE;
  id: string;
  draft: Draft;
}
const keptDraft = (d: RecipeDraft | null | undefined): Draft | null => {
  const kept = d?.state as Partial<Kept> | undefined;
  return kept?.v === DRAFT_SHAPE && kept.draft ? kept.draft : null;
};
export const newCopyKey = (draftId: string) => `bl.recipe-draft.${draftId}`;
export const editCopyKey = (recipeId: string) => `bl.recipe-edit.${recipeId}`;
export interface Start {
  draftId: string;
  draft: Draft;
  /** When the draft picked up was last saved, if one was. */
  resumedFrom: string | null;
  /** Typed on this device and never sent: sent as soon as the page opens. */
  unsent: boolean;
}
/** The newer of what the server kept and what this device kept, else the page fresh. */
export function startFrom(
  copyKey: string,
  server: RecipeDraft | null | undefined,
  fresh: { draftId: string; draft: Draft },
): Start {
  const stored = readLocal<Copy>(copyKey);
  const local = stored?.value?.v === DRAFT_SHAPE ? stored : null;
  const kept = keptDraft(server);
  if (local && (!server || Date.parse(local.savedAt) > Date.parse(server.savedAt))) {
    return {
      draftId: local.value.id,
      draft: local.value.draft,
      resumedFrom: local.savedAt,
      unsent: true,
    };
  }
  // A copy no newer than the server's was sent as the page closed, and the page never lived to clear
  // it; a copy in another shape is let go.
  if (stored) clearLocal(copyKey);
  if (server && kept) {
    return { draftId: server.id, draft: kept, resumedFrom: server.savedAt, unsent: false };
  }
  return { ...fresh, resumedFrom: null, unsent: false };
}
export const mintId = () => newKey();
